import { useRef } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Plus, ExternalLink, Loader2, FileText } from 'lucide-react';

/**
 * Per-item receipt upload / view button.
 *
 * Props:
 *  - itemId
 *  - isUploading        boolean, true while this row is uploading
 *  - linked             array of receipt objects already attached to this item
 *  - onUpload(file)     fires when user picks a file
 *  - onViewReceipt(r)   fires when a linked receipt is opened in the side panel
 */
export const ReceiptUploadBtn = ({ itemId, isUploading, linked = [], onUpload, onViewReceipt }) => {
  const fileRef = useRef(null);
  const count = linked.length;

  const hiddenInput = (
    <input
      ref={fileRef}
      type="file"
      accept="image/*,application/pdf"
      className="hidden"
      onChange={e => {
        const f = e.target.files?.[0];
        if (f) onUpload(f);
        e.target.value = '';
      }}
      data-testid={`receipt-file-input-${itemId}`}
    />
  );

  // Zero receipts: click = upload.
  if (count === 0) {
    return (
      <div className="flex items-center gap-1">
        {hiddenInput}
        <button
          onClick={() => fileRef.current?.click()}
          disabled={isUploading}
          className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded-md transition-all ${
            isUploading
              ? 'bg-[#5b6773]/10 text-[#5b6773] cursor-wait'
              : 'bg-[#8b6b3a]/10 text-[#8b6b3a] hover:bg-[#8b6b3a]/20'
          }`}
          title="Upload receipt"
          data-testid={`upload-receipt-btn-${itemId}`}
        >
          {isUploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />}
          {isUploading ? '' : 'Receipt'}
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {hiddenInput}
      <Popover>
        <PopoverTrigger asChild>
          <button
            className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-md bg-[#2f6f8f]/10 text-[#2f6f8f] hover:bg-[#2f6f8f]/20 transition-all"
            title={`View ${count} receipt${count === 1 ? '' : 's'} for this item`}
            data-testid={`view-receipts-btn-${itemId}`}
          >
            <FileText className="h-3 w-3" />
            Receipt · {count}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="end" data-testid={`receipts-popover-${itemId}`}>
          <div className="max-h-[360px] overflow-y-auto divide-y">
            {linked.map(r => {
              const itemSummary = (r.line_item_summary || []).find(s => s.item_id === itemId);
              const contributions = itemSummary?.lines || [];
              const contributedAmount = itemSummary?.amount;
              return (
                <div key={r.id} className="p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => onViewReceipt(r)}
                      className="inline-flex items-center gap-1.5 text-[#2f6f8f] hover:underline text-sm min-w-0 text-left"
                      data-testid={`view-receipt-${r.id}`}
                    >
                      <FileText className="h-3.5 w-3.5 flex-shrink-0" />
                      <span className="truncate">{r.vendor || r.file_name || 'Receipt'}</span>
                      <ExternalLink className="h-3 w-3 opacity-60 flex-shrink-0" />
                    </button>
                    {contributedAmount != null && (
                      <span className="text-[11px] tabular-nums font-semibold text-[#1f2933] bg-[#2d948c]/10 px-2 py-0.5 rounded-full flex-shrink-0">
                        ${Math.round(Number(contributedAmount)).toLocaleString('en-US')}
                      </span>
                    )}
                  </div>
                  {contributions.length > 0 ? (
                    <div className="space-y-1 pl-5">
                      <p className="text-[10px] uppercase tracking-wider text-[#5b6773]/80">Line items</p>
                      {contributions.map((ln, i) => (
                        <div key={i} className="flex items-center justify-between text-[11px] text-[#5b6773]">
                          <span className="truncate pr-2">{ln.line_item_name || '—'}{ln.count > 1 ? ` × ${ln.count}` : ''}</span>
                          <span className="tabular-nums text-[#1f2933] flex-shrink-0">${Math.round(Number(ln.cost || 0)).toLocaleString('en-US')}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    r.amount != null && (
                      <p className="text-[11px] text-[#5b6773] pl-5">
                        Receipt total: <span className="tabular-nums text-[#1f2933]">${Math.round(Number(r.amount)).toLocaleString('en-US')}</span>
                      </p>
                    )
                  )}
                </div>
              );
            })}
          </div>
          <div className="p-2 border-t bg-[#f3efe6]/30">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={isUploading}
              className="w-full flex items-center justify-center gap-1.5 text-[11px] px-2 py-1.5 rounded bg-white border hover:bg-[#2d948c]/5 text-[#2d948c]"
              data-testid={`add-receipt-from-popover-${itemId}`}
            >
              {isUploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
              Add another receipt
            </button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
